import { Icon } from "./icons";
import { useNavPrefs } from "../lib/navPrefs";
import { DEFAULT_ACCENT, NAV_ACCENTS, readableOn, arrangeNav, moveNavItem } from "@shared/nav";

// 表示・テーマ（自分の画面）。LocoWorks の設定と同じ操作：サイドメニュー全体の色＋項目ごとの表示・色・並び順。
// 並び替えはサイドメニューで直接（PCはドラッグ・スマホは長押し）でもでき、ここの ↑↓ はキーボード・読み上げでも使える代わりの手段。
// 自分の画面だけに反映（全員のメニューは 設定 →「サイドメニューのカスタマイズ（全員）」）。
const DEFAULT_ICON = "#374151";

export default function NavPrefsEditor() {
  const ctl = useNavPrefs();
  if (!ctl) return null;
  const { prefs, available, update, status, error } = ctl;
  const accent = prefs.accent ?? DEFAULT_ACCENT;
  const arranged = arrangeNav(available, prefs);
  const items = arranged.flatMap((g) => g.items);
  const move = (key: string, group: string, before: string | null) =>
    update((p) => ({ ...p, layout: moveNavItem(arrangeNav(available, p), key, group, before) }));
  // ↑：前の項目の前へ（グループの先頭なら前のグループの最後へ）。↓：次の項目の後へ（最後なら次のグループの先頭へ）
  const up = (gi: number, idx: number) => {
    const g = arranged[gi], key = g.items[idx].to;
    if (idx > 0) move(key, g.id, g.items[idx - 1].to);
    else if (gi > 0) move(key, arranged[gi - 1].id, null);
  };
  const down = (gi: number, idx: number) => {
    const g = arranged[gi], key = g.items[idx].to;
    if (idx < g.items.length - 1) move(key, g.id, g.items[idx + 2]?.to ?? null);
    else if (gi < arranged.length - 1) move(key, arranged[gi + 1].id, arranged[gi + 1].items[0]?.to ?? null);
  };

  const setItem = (to: string, patch: { hidden?: boolean; color?: string | null }) =>
    update((p) => ({ ...p, items: { ...(p.items ?? {}), [to]: { ...(p.items?.[to] ?? {}), ...patch } } }), { debounce: "color" in patch && patch.color !== null });
  const shownCount = items.filter((i) => !prefs.items?.[i.to]?.hidden).length;

  return (
    <div className="space-y-5">
      <section>
        <h3 className="mb-2 text-sm font-semibold text-gray-700">サイドメニュー全体の色（選択中の項目）</h3>
        <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label="サイドメニュー全体の色">
          {NAV_ACCENTS.map((a) => {
            const on = accent === a.hex;
            return (
              <button key={a.hex} type="button" role="radio" aria-checked={on} aria-label={a.name} title={a.name}
                className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition ${on ? "border-gray-800 ring-2 ring-offset-2" : "border-white shadow"}`}
                style={{ backgroundColor: a.hex, color: readableOn(a.hex) }}
                onClick={() => update((p) => ({ ...p, accent: a.hex }))}>
                {on && <Icon name="check" className="h-5 w-5" />}
              </button>
            );
          })}
          <label className="ml-1 flex items-center gap-1.5 text-xs text-gray-500">カスタム
            <input type="color" className="h-10 w-12 cursor-pointer rounded border border-gray-200 bg-white p-0.5" value={accent}
              onChange={(e) => update((p) => ({ ...p, accent: e.target.value }), { debounce: true })} aria-label="サイドメニュー全体の色（カスタム）" />
          </label>
        </div>
      </section>

      <section>
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-700">メニュー項目ごとの表示・色（よく使う項目を目立たせる）</h3>
          <span className="text-xs text-gray-400">表示中 {shownCount}/{items.length}</span>
        </div>
        <p className="mb-2 text-xs text-gray-500">並び順は、サイドメニューの項目を<b>ドラッグ</b>（スマホは<b>長押し</b>して移動）でも変えられます。グループの見出しを押すと折りたためます。</p>
        <div className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
          {arranged.map((g, gi) => (
            <div key={g.id}>
              <div className="mb-0.5 px-1 text-[11px] font-semibold text-gray-400">{g.label ?? "トップ"}</div>
              {g.items.map((i, idx) => {
                const c = prefs.items?.[i.to] ?? {};
                const shown = !c.hidden;
                const first = gi === 0 && idx === 0;
                const last = gi === arranged.length - 1 && idx === g.items.length - 1;
                return (
                  <div key={i.to} className="flex min-h-[44px] items-center gap-1.5 rounded-lg px-1 hover:bg-gray-50">
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 py-1.5 text-sm">
                      <input type="checkbox" className="h-4 w-4 shrink-0 accent-brand-600" checked={shown}
                        onChange={(e) => setItem(i.to, { hidden: !e.target.checked })} />
                      <span className="shrink-0" style={{ color: c.color ?? DEFAULT_ICON }}><Icon name={i.icon} className="h-5 w-5" /></span>
                      <span className={`truncate ${shown ? "text-gray-800" : "text-gray-400 line-through"}`}>{i.label}</span>
                    </label>
                    <button type="button" className="h-8 w-7 shrink-0 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-20" disabled={first} onClick={() => up(gi, idx)} aria-label={`${i.label}を上へ`} title="上へ">↑</button>
                    <button type="button" className="h-8 w-7 shrink-0 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-20" disabled={last} onClick={() => down(gi, idx)} aria-label={`${i.label}を下へ`} title="下へ">↓</button>
                    {c.color && (
                      <button type="button" className="shrink-0 text-[11px] text-gray-400 underline" onClick={() => setItem(i.to, { color: null })} aria-label={`${i.label}の色を元に戻す`}>戻す</button>
                    )}
                    <input type="color" className="h-8 w-10 shrink-0 cursor-pointer rounded border border-gray-200 bg-white p-0.5" value={c.color ?? DEFAULT_ICON}
                      onChange={(e) => setItem(i.to, { color: e.target.value })} aria-label={`${i.label}の色`} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
        <p className="text-xs text-gray-500">
          自分の画面だけに反映され、自動で保存されます（PC・スマホ共通）。非表示にした画面も、上の検索から開けます。
        </p>
        <div className="flex items-center gap-3">
          <span className="text-xs" role="status" aria-live="polite">
            {status === "saving" && <span className="text-gray-400">保存中…</span>}
            {status === "saved" && <span className="text-brand-700">✓ 保存しました</span>}
            {status === "error" && <span className="text-red-600">⚠ {error || "保存できませんでした"}</span>}
          </span>
          {(prefs.layout || prefs.folded) && (
            <button type="button" className="btn-secondary text-xs" onClick={() => update((p) => ({ ...p, layout: undefined, folded: undefined }))}>並び順・折りたたみを元に戻す</button>
          )}
          <button type="button" className="btn-secondary text-xs" onClick={() => { if (confirm("表示・色・並び順をすべて初期状態に戻します。よろしいですか？")) update(() => ({})); }}>すべて初期状態に戻す</button>
        </div>
      </div>
    </div>
  );
}
